import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePhone } from "../phone/usePhone.js";
import PhoneNcr from "../phone/PhoneNcr.jsx";
import { describeRaiseOutcome, describeNcrExportOutcome, ncrExportCodes, runNcrRegisterExports, createRaiseGate, photoCountLabel, ncrStatusLabel, historyCacheKeyFor } from "../api/shape.js";

const PREVIEW_ERROR_TEXT = {
  no_yard_recipient: "no yard recipient configured for this project",
  external_send_blocked: "recipient is outside the allowed send domain",
  stale_etag: "this NCR changed since it was read — refresh and try again",
  mail_unavailable: "mail is not available for this project right now",
};

const CHEVRON = (
  <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);
const CHECK = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0a0f1a" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.5l4.5 4.5L19 7"></path>
  </svg>
);

// State filter (owner live-use defect: ~400 NCRs in the capture app, only 1
// visible in the cockpit — the default "To decide" tab only ever shows
// `Recorded` rows; everything that has moved past that state was invisible
// with no way to see it). "Returned" narrows "To decide" to the rows the SM
// sent back to the inspector and is still awaiting resubmit; "All" is every
// row from every other tab, so the full count is always reachable. Both are
// derived client-side from `model.tabs` (shapeNcrScreen's existing four
// buckets, unchanged) — GET /api/projects/{code}/ncrs (CisNcrsQuery) already
// returns every NcrStatus, not just undecided ones.
const TABS = [
  { key: "toDecide", label: "To decide" },
  { key: "returned", label: "Returned" },
  { key: "raised", label: "Raised, awaiting yard" },
  { key: "answered", label: "Answered, our move" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

const STATE_CHIPS = ["Recorded", "Raised to yard", "Answered", "Verified", "Closed"];

function Checkbox({ checked, onChange }) {
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onClick={onChange}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onChange();
        }
      }}
      style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        border: `1px solid ${checked ? "var(--acc)" : "var(--line2)"}`,
        background: checked ? "var(--acc)" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        cursor: "pointer",
      }}
    >
      {checked ? CHECK : null}
    </div>
  );
}

function Row({ row, selectable, selected, active, onToggleSelect, onSetActive }) {
  return (
    <div className="r" style={{ background: active ? "var(--raised)" : undefined, cursor: "pointer" }} onClick={() => onSetActive(row.key)}>
      {selectable ? (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected} onChange={() => onToggleSelect(row.key)} />
        </div>
      ) : null}
      <div className="mono" style={{ fontSize: 11, color: "var(--t3)", width: 56, flexShrink: 0 }}>{row.dateLabel}</div>
      <div className="proj">{row.code}</div>
      <div className="ttl">{row.title}</div>
      <div className="who">{row.who}</div>
      {photoCountLabel(row.photoCount) ? (
        <div className="mono" style={{ fontSize: 11, color: "var(--t3)", width: 60, flexShrink: 0 }}>
          {photoCountLabel(row.photoCount)}
        </div>
      ) : null}
      {selectable ? (
        selected ? (
          <div className="chip acc">selected</div>
        ) : row.awaitingResubmit ? (
          // To-decide rows are always selectable, so this is the only place
          // an un-selected "Returned to inspector" row shows its marker.
          <div className="chip warn">Returned</div>
        ) : (
          <div style={{ width: 64 }}></div>
        )
      ) : (
        <div className={`chip ${row.awaitingResubmit ? "warn" : "mute"}`}>{ncrStatusLabel(row)}</div>
      )}
      {CHEVRON}
    </div>
  );
}

/**
 * NCR screen — mockups/cockpit-v3/NCR.dc.html, element for element. `model`
 * is shape.js#shapeNcrScreen's output (live) or the fixture equivalent — both
 * render through this one component, same discipline as Today/Plan.
 *
 * The photo count column the artboard shows on each row (`row.photoCount`,
 * from `GET /api/projects/{code}/ncrs` -> `CisNcrsQuery`, computed
 * server-side from `NcrPhotos-<CODE>` in one batched read) renders as
 * "N photos"/"1 photo", omitted when a row has none.
 *
 * The preview pane (right column) never approximates: it renders the real
 * `POST /api/cockpit/ncrs/raise?preview=1` response (`onPreview` prop,
 * App.jsx `handleNcrPreview`) verbatim — recipients, subject, body — for
 * whichever rows are selected (or the active row alone). A blocking preview
 * error (`no_yard_recipient`, `external_send_blocked`, `stale_etag`,
 * `mail_unavailable`) disables "Raise N to yard" for that project.
 */
export default function NCR({
  model,
  initialSelectedKeys = [],
  initialActiveKey = null,
  initialExportResults = null,
  onSaveDescription,
  onReturn,
  onDismiss,
  onRaise,
  onResolve,
  onPreview,
  onLoadHistory,
  onExport,
}) {
  const phone = usePhone();
  const [tab, setTab] = useState("toDecide");
  const [selected, setSelected] = useState(() => new Set(initialSelectedKeys));
  const [activeKey, setActiveKey] = useState(initialActiveKey);
  const [noteDraft, setNoteDraft] = useState(null); // { action: "return"|"dismiss", text }
  const [pending, setPending] = useState(null);
  const raiseGate = useRef(null);
  if (!raiseGate.current) raiseGate.current = createRaiseGate();
  const [raiseResult, setRaiseResult] = useState(null);
  const [refuseNotice, setRefuseNotice] = useState(null);
  const [previewByCode, setPreviewByCode] = useState({});
  const [previewPending, setPreviewPending] = useState(false);
  const [wordingDraft, setWordingDraft] = useState(null); // { key, text } | null — in-progress edit for the active row's wording only
  const [historyByKey, setHistoryByKey] = useState({}); // { [row.key]: [{returnId, returnNote, returnedByUpn, returnedAtUtc, status}] }
  const [exportResults, setExportResults] = useState(initialExportResults); // runNcrRegisterExports output, one row per project

  // "Returned" and "All" are purely client-side derivations of the same
  // four buckets shapeNcrScreen already produces — never a separate read,
  // never a reshuffle of `model.tabs` itself (shape.ncr.test.js asserts its
  // exact shape).
  const rowsByTab = useMemo(() => {
    const base = model.tabs || { toDecide: [], raised: [], answered: [], closed: [] };
    const all = [...base.toDecide, ...base.raised, ...base.answered, ...base.closed].sort(
      (a, b) => (a.dateLabel < b.dateLabel ? -1 : a.dateLabel > b.dateLabel ? 1 : a.ncrNumber - b.ncrNumber)
    );
    return {
      ...base,
      returned: base.toDecide.filter((r) => r.awaitingResubmit),
      all,
    };
  }, [model.tabs]);

  const rows = rowsByTab[tab] || [];
  const active = useMemo(() => rows.find((r) => r.key === activeKey) || rows[0] || null, [rows, activeKey]);
  const selectedRows = rows.filter((r) => selected.has(r.key));
  const wordingValue = active ? (wordingDraft && wordingDraft.key === active.key ? wordingDraft.text : active.description) : "";

  // The preview pane never approximates recipients/subject/body — it always
  // reflects the real POST /api/cockpit/ncrs/raise?preview=1 response
  // (App.jsx handleNcrPreview) for whichever rows are currently in play (the
  // selection, or the single active row when nothing is selected yet).
  // Wording is saved via PATCH on blur BEFORE any preview reflects it
  // (`saveActiveDescription`) — this effect re-fires once that save
  // completes and the row's fresh etag flows back through `model` (§4.5: a
  // preview against a stale etag is refused), so the preview is never stale
  // relative to what a raise would actually send.
  useEffect(() => {
    const targets = tab === "toDecide" ? (selectedRows.length ? selectedRows : active ? [active] : []) : [];
    if (!targets.length || !onPreview) {
      setPreviewByCode({});
      return;
    }
    let cancelled = false;
    setPreviewPending(true);
    onPreview(targets).then((byCode) => {
      if (cancelled) return;
      setPreviewByCode(byCode || {});
      setPreviewPending(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, active && active.key, selectedRows.map((r) => `${r.key}:${r.etag}`).join(","), onPreview]);

  const activePreview = active ? previewByCode[active.code] : null;
  const blockingCodes = [...new Set(selectedRows.map((r) => r.code))].filter(
    (code) => previewByCode[code] && previewByCode[code].status === "failed"
  );

  // SPEC-CIS-NCR-RETURN-RESUBMIT-001 §4.7: the note/history alongside the
  // existing single-note display — one history GET for the active row,
  // only when it has ever been returned (awaitingResubmit, or it still
  // carries a mirrored returnNote from a resolved return). Cached by
  // `${key}:${etag}` (codex review finding #3, PR #1950), NOT by `key`
  // alone: a later return/resubmit changes the NCR row's etag (every
  // mutating action reloads via loadNcrs()), so a stale cache entry keyed
  // only by `key` kept showing an earlier return as `Open` and omitted a
  // later one — this makes the cache self-invalidate on every write to the
  // row, exactly the signal I3/AC-18 already treats as authoritative.
  const historyCacheKey = historyCacheKeyFor(active);
  useEffect(() => {
    if (!active || !onLoadHistory || !(active.awaitingResubmit || active.returnNote)) return;
    if (historyByKey[historyCacheKey]) return;
    let cancelled = false;
    onLoadHistory(active).then((rows) => {
      if (cancelled) return;
      setHistoryByKey((prev) => ({ ...prev, [historyCacheKey]: rows || [] }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyCacheKey, onLoadHistory]);

  const activeHistory = historyCacheKey ? historyByKey[historyCacheKey] : null;

  function selectTab(key) {
    setTab(key);
    setActiveKey(null);
    setRaiseResult(null);
  }

  function toggleSelect(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function saveActiveDescription(text) {
    // Trim leading/trailing whitespace only — internal newlines are the
    // wording's own paragraph breaks and must reach the server unchanged
    // (validation.js normalizeMultiline only canonicalises \r\n/\r to \n,
    // it never strips them).
    const trimmed = text.trim();
    if (!active || trimmed === active.description) return;
    setPending("save");
    await onSaveDescription(active, trimmed);
    setPending(null);
    setWordingDraft(null);
  }

  async function confirmNote() {
    if (!noteDraft || !noteDraft.text.trim() || !active) return;
    setPending(noteDraft.action);
    if (noteDraft.action === "return") await onReturn(active, noteDraft.text.trim());
    else await onDismiss(active, noteDraft.text.trim());
    setPending(null);
    setNoteDraft(null);
  }

  async function handleRaise() {
    if (selectedRows.length === 0) return;
    if (selectedRows.length > 50) {
      setRefuseNotice("Select 50 or fewer NCRs to raise at once.");
      return;
    }
    if (blockingCodes.length > 0) return; // Raise button is disabled in this state; defensive no-op.
    setRefuseNotice(null);
    const id = raiseGate.current.begin();
    if (!id) return; // a raise is already in flight: a double tap is not a second raise
    setPending("raise");
    let result;
    let allTerminal = false;
    try {
      result = await onRaise(selectedRows, id);
      allTerminal = Boolean(result && result.projects && result.projects.every((p) => p.status === "sent" || p.status === "duplicate"));
    } finally {
      raiseGate.current.end(allTerminal);
      setPending(null);
    }
    setRaiseResult(result);
    if (allTerminal) setSelected(new Set());
  }

  // "Export NCR register" (EP-CIS-004, DEC-CHAT-20261010-404): the
  // yard-transmission package the capture app used to build, moved here.
  // One POST /api/projects/{code}/ncrs/exports per project on screen (the
  // same per-project pattern as Raise); the server's own default filter is
  // Open + Answered, and the package is the register .xlsx + photos.
  const exportCodes = useMemo(() => ncrExportCodes(model.tabs), [model.tabs]);

  async function handleExport() {
    if (!onExport || exportCodes.length === 0 || pending === "export") return;
    setPending("export");
    setExportResults(null);
    const results = await runNcrRegisterExports(exportCodes, onExport);
    setPending(null);
    setExportResults(results);
  }

  async function handleResolve(project, outcome) {
    setPending(`resolve-${project.runId}`);
    const result = await onResolve(project.runId, outcome);
    setPending(null);
    if (raiseResult) {
      setRaiseResult({
        ...raiseResult,
        projects: raiseResult.projects.map((p) => (p.runId === project.runId ? { ...p, status: result.status } : p)),
      });
    }
  }

  const projectCounts = TABS.reduce((acc, { key }) => {
    acc[key] = (rowsByTab[key] || []).length;
    return acc;
  }, {});

  const distinctProjects = new Set(selectedRows.map((r) => r.code)).size;

  // Phone layout (Ncr.dc.html and its detail/raise/return screens): the same
  // state and handlers as below, drawn as cards, a detail screen and sheets.
  // Arriving with an NCR already chosen ("Open NCR" from Today/Plan/Report)
  // opens that NCR's detail, on the tab that holds it.
  const initialPhoneTab = useMemo(
    () => (initialActiveKey ? ["toDecide", "raised", "answered", "closed"].find((k) => (rowsByTab[k] || []).some((r) => r.key === initialActiveKey)) || null : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    if (phone && initialPhoneTab && initialPhoneTab !== "toDecide") setTab(initialPhoneTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (phone) {
    return (
      <PhoneNcr
        ctx={{
          model, TABS, tab, selectTab, projectCounts, rows, selected, selectedRows, toggleSelect,
          selectOnly: (key) => setSelected(new Set([key])),
          active, setActiveKey, onExport: onExport || null, handleExport, exportCodes, exportResults,
          describeExport: describeNcrExportOutcome, pending, setRaiseResult, raiseResult, handleRaise, handleResolve,
          noteDraft, setNoteDraft, confirmNote, activeHistory, wordingValue, setWordingDraft, saveActiveDescription,
          activePreview, previewPending, previewByCode, blockingCodes, refuseNotice,
          previewErrorText: (code) => {
            const p = previewByCode[code];
            return (p && (PREVIEW_ERROR_TEXT[p.error] || p.error)) || "preview unavailable";
          },
          initialDetail: Boolean(initialActiveKey),
        }}
      />
    );
  }

  return (
    <div className="page" style={{ flexDirection: "row" }}>
      <div className="col" style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
          <div className="h1">NCRs</div>
          <div className="lead">inspectors record · you review, adjust the wording if needed, and raise to the yard in one send</div>
        </div>

        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={`tab ${tab === t.key ? "on" : ""}`} onClick={() => selectTab(t.key)}>
              {t.label}
              {projectCounts[t.key] !== undefined ? <span className="mono" style={{ fontSize: 11, color: tab === t.key ? "var(--t1)" : "var(--t3)" }}>{projectCounts[t.key]}</span> : null}
            </button>
          ))}
        </div>

        {model.errors.length > 0 ? (
          <div className="list" data-testid="ncr-errors">
            {model.errors.map((e) => (
              <div className="r" key={e.code}>
                <div className="chip bad age">!</div>
                <div className="proj">{e.code}</div>
                <div className="ttl" style={{ color: "var(--bad)" }}>Unavailable &mdash; {e.error}</div>
              </div>
            ))}
          </div>
        ) : null}

        <div className="list">
          {rows.length === 0 ? (
            <div className="r"><div className="ttl" style={{ color: "var(--t3)" }}>Nothing to do</div></div>
          ) : (
            rows.map((row) => (
              <Row
                key={row.key}
                row={row}
                selectable={tab === "toDecide"}
                selected={selected.has(row.key)}
                active={active && active.key === row.key}
                onToggleSelect={toggleSelect}
                onSetActive={setActiveKey}
              />
            ))
          )}
        </div>

        {tab === "toDecide" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {selected.size > 0 ? (
              <button
                type="button"
                className="btn acc"
                style={{ minHeight: 36 }}
                disabled={pending === "raise" || blockingCodes.length > 0}
                onClick={handleRaise}
              >
                {pending === "raise" ? "…" : `Raise ${selected.size} to yard`}
              </button>
            ) : null}
            <button
              type="button"
              className="btn"
              disabled={!active}
              onClick={() => setNoteDraft({ action: "return", text: "" })}
            >
              Return to inspector
            </button>
            <button
              type="button"
              className="btn off"
              disabled={!active}
              onClick={() => setNoteDraft({ action: "dismiss", text: "" })}
            >
              Dismiss
            </button>
            {selected.size > 1 ? (
              <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--t3)" }}>
                {distinctProjects > 1
                  ? `${distinctProjects} sends, one per project mailbox and yard counterpart`
                  : "one send, from the project mailbox"}
              </div>
            ) : null}
          </div>
        ) : null}

        {refuseNotice ? <div data-testid="ncr-refuse-notice" style={{ color: "var(--bad)", fontSize: 12 }}>{refuseNotice}</div> : null}

        {blockingCodes.length > 0 ? (
          <div className="list" data-testid="ncr-preview-blocking">
            {blockingCodes.map((code) => (
              <div className="r" key={code}>
                <div className="chip bad age">!</div>
                <div className="proj">{code}</div>
                <div className="ttl" style={{ color: "var(--bad)" }}>
                  Cannot raise &mdash; {PREVIEW_ERROR_TEXT[previewByCode[code].error] || previewByCode[code].error || "preview unavailable"}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {noteDraft ? (
          <div className="card" data-testid="ncr-note-draft">
            <div className="t">{noteDraft.action === "return" ? "Return to inspector" : "Dismiss"}</div>
            <textarea
              value={noteDraft.text}
              onChange={(e) => setNoteDraft({ ...noteDraft, text: e.target.value })}
              placeholder="Note (required)"
              style={{ minHeight: 60, background: "var(--raised)", color: "var(--t1)", border: "1px solid var(--line2)", borderRadius: 6, padding: 8, font: "inherit" }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn acc" disabled={!noteDraft.text.trim() || pending === noteDraft.action} onClick={confirmNote}>
                Confirm
              </button>
              <button type="button" className="btn off" onClick={() => setNoteDraft(null)}>Cancel</button>
            </div>
          </div>
        ) : null}

        {active && (active.awaitingResubmit || active.returnNote) ? (
          <div className="card" data-testid="ncr-return-history">
            <div className="t">Return history</div>
            {activeHistory === null || activeHistory === undefined ? (
              <div style={{ fontSize: 12, color: "var(--t3)" }}>Loading…</div>
            ) : activeHistory.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--t3)" }}>{active.returnNote}</div>
            ) : (
              activeHistory.map((h) => (
                <div key={h.returnId} style={{ fontSize: 12, borderTop: "1px solid var(--line2)", paddingTop: 6, marginTop: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "var(--t3)" }}>
                    <span>{h.returnedByUpn}</span>
                    <span className={`chip ${h.status === "Open" ? "warn" : "mute"}`} style={{ fontSize: 10 }}>{h.status}</span>
                  </div>
                  <div>{h.returnNote}</div>
                </div>
              ))
            )}
          </div>
        ) : null}

        {raiseResult ? (
          <div className="list" data-testid="ncr-raise-outcomes">
            {raiseResult.projects.map((project) => (
              <div className="r" key={project.code}>
                <div className="proj">{project.code}</div>
                <div className="ttl">{describeRaiseOutcome(project)}</div>
                {project.status === "unknown" ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button type="button" className="btn" disabled={pending === `resolve-${project.runId}`} onClick={() => handleResolve(project, "sent")}>
                      Resolve: sent
                    </button>
                    <button type="button" className="btn off" disabled={pending === `resolve-${project.runId}`} onClick={() => handleResolve(project, "not_sent")}>
                      Resolve: not sent
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {/* Rendered only when an exporter is wired: App.jsx passes onExport
            in live mode only, so fixture mode (the artboard-conformance
            twin, scripts/verifiers/cis-cockpit/v-artboard.js) stays
            element-for-element on NCR.dc.html — the same fixture-gating
            Plan already uses for onDetailSelect/onShowMoreBacklog. */}
        {onExport ? (
          <div data-testid="ncr-export" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              className="btn"
              style={{ minHeight: 36 }}
              disabled={pending === "export" || exportCodes.length === 0}
              onClick={handleExport}
            >
              {pending === "export" ? "Exporting…" : "Export NCR register"}
            </button>
            <div style={{ fontSize: 11, color: "var(--t3)" }}>
              Excel format · Open + Answered · register + photos
              {exportCodes.length > 1 ? ` · one register per project (${exportCodes.length})` : ""}
            </div>
          </div>
        ) : null}

        {onExport && pending === "export" ? (
          <div data-testid="ncr-export-progress" style={{ fontSize: 12, color: "var(--t3)" }}>
            Building the register package (register + photos)…
          </div>
        ) : null}

        {onExport && exportResults ? (
          <div className="list" data-testid="ncr-export-results">
            {exportResults.map((result) => (
              <div className="r" key={result.code}>
                <div className="proj">{result.code}</div>
                <div className="ttl" style={result.status === "done" ? undefined : { color: "var(--bad)" }}>
                  {describeNcrExportOutcome(result)}
                </div>
                {result.status === "done" && typeof result.fileUrl === "string" && /^https:\/\//.test(result.fileUrl) ? (
                  <a
                    href={result.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn"
                    style={{ minHeight: 26, fontSize: 11, textDecoration: "none" }}
                  >
                    Open register
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <div className="card">
          <div className="t">How an NCR moves</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {STATE_CHIPS.map((label, i) => (
              <React.Fragment key={label}>
                <div className={`chip ${i === 0 ? "warn" : "mute"}`} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, padding: "4px 10px" }}>{label}</div>
                {i < STATE_CHIPS.length - 1 ? CHEVRON : null}
              </React.Fragment>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "var(--t3)", lineHeight: 1.5 }}>
            Raising assigns the NCR number, renders the NCR PDF, and sends one standard email per project from that project&apos;s mailbox with the PDFs attached. The sent date is stored on each NCR. The yard replies by ordinary email; the answer is recorded against the NCR from the reply, as builder responses are in DAS. No inbox in the cockpit.
          </div>
        </div>
      </div>

      <div className="col" style={{ width: 420, flexShrink: 0, gap: 12 }}>
        {active ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className="mono" style={{ fontSize: 11, color: "var(--t3)", flexGrow: 1 }}>
                preview · {active.code} · from {(activePreview && activePreview.recipients && activePreview.recipients.from) || (previewPending ? "…" : "—")}
              </div>
              <div className="chip mute">standard text</div>
            </div>
            <div className="field" style={activePreview && activePreview.status === "failed" ? { borderColor: "var(--bad)", color: "var(--bad)" } : undefined}>
              <span className="l">to</span>
              {activePreview && activePreview.status === "ready" ? (
                <>
                  <span className="mono" style={{ fontSize: 11 }}>{activePreview.recipients.to.join(", ")}</span> · cc{" "}
                  <span className="mono" style={{ fontSize: 11 }}>{activePreview.recipients.cc.join(", ")}</span>
                </>
              ) : activePreview && activePreview.status === "failed" ? (
                `Cannot raise — ${PREVIEW_ERROR_TEXT[activePreview.error] || activePreview.error}`
              ) : previewPending ? (
                "Loading preview…"
              ) : (
                "—"
              )}
            </div>
            <div className="field">
              <span className="l">subject</span>
              {activePreview && activePreview.status === "ready" ? activePreview.subject : ""}
            </div>
            <div className="field" style={{ minHeight: 150, lineHeight: 1.5 }}>
              <span className="l">Mail text the yard receives</span>
              {activePreview && activePreview.status === "ready" ? (
                activePreview.coverText.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {i > 0 ? <br /> : null}
                    {line}
                  </React.Fragment>
                ))
              ) : previewPending ? (
                // The label never sits above an empty box while the real
                // POST /api/cockpit/ncrs/raise?preview=1 read is in flight
                // (owner feedback: the unlabelled blank area read as broken,
                // not loading).
                "Loading…"
              ) : null}
            </div>
            <div className="field" style={{ borderColor: "var(--acc)" }}>
              <label className="l" htmlFor="ncr-wording">NCR wording, editable before raising</label>
              {/* A controlled <textarea>, not contentEditable: a plain text
                  node's `textContent` on blur does not reliably reproduce
                  typed line breaks (a contentEditable div represents a
                  visual line break as a child element, not a "\n"
                  character), which silently dropped multi-paragraph edits.
                  A textarea's `.value` always preserves exactly what was
                  typed. Saved on blur (`onSaveDescription`), never on every
                  keystroke, matching "wording is saved via PATCH before
                  raise" — the preview effect above re-fires once that
                  save's fresh etag comes back through `model`. The artboard
                  gate reads a form control's `.value` for this region
                  (scripts/verifiers/cis-cockpit/v-artboard.js), not
                  `innerText`, so this stays text-conformance-checked. */}
              <textarea
                id="ncr-wording"
                aria-label="NCR wording, editable before raising"
                aria-multiline="true"
                key={active.key}
                value={wordingValue}
                onChange={(e) => setWordingDraft({ key: active.key, text: e.target.value })}
                onBlur={(e) => saveActiveDescription(e.target.value)}
                disabled={active.status !== "Recorded" || Boolean(active.raiseRunId)}
                style={{ width: "100%", minHeight: 70, background: "transparent", color: "inherit", border: "none", outline: "none", resize: "vertical", font: "inherit", padding: 0 }}
              />
            </div>
            <div style={{ fontSize: 11, color: "var(--t3)", lineHeight: 1.5, marginTop: "auto" }}>
              The covering text is fixed. Only the NCR wording and the recipient can change, and the recipient comes from the project&apos;s parties list.
            </div>
          </>
        ) : (
          <div style={{ color: "var(--t3)", fontSize: 12 }}>Select an NCR to preview.</div>
        )}
      </div>
    </div>
  );
}
